import { ChangeEvent, useCallback, useState } from 'react';
import * as Yup from 'yup';
import { collectValidationErrors, ValidationErrors } from 'src/constants/validations';

/**
 * Only what this hook asks of a schema. Yup's own object type carries the shape it was built from, and two
 * forms of different shapes cannot both be that type.
 */
type SignupFormSchema<T> = {
  validate: (values: T, options: { abortEarly: boolean }) => Promise<unknown>;
};

/**
 * The plumbing the two public forms share: the values, the complaints about each field, and the one that
 * belongs to the form as a whole. Only the plumbing — what a form does with what it collected is its own
 * controller's, and the account step and the company step do entirely different things with it.
 */
export const useSignupForm = <T extends Record<string, string>>(
  emptyForm: T,
  schema: SignupFormSchema<T>,
) => {
  const [formValues, setFormValues] = useState<T>(emptyForm);
  const [errors, setErrors] = useState<ValidationErrors>({});
  // Kept apart from the field errors: it belongs to the whole form, and a key in the same record would be
  // one field name away from being rendered as a complaint about an input.
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target as { name: string; value: string };
    setFormValues(current => ({ ...current, [name]: value }));

    // The complaint was about what the field held a keystroke ago, and the whole form is validated again on
    // submit anyway.
    setErrors(current => {
      if (current[name] == null) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
  }, []);

  const validate = useCallback(async(values: T): Promise<boolean> => {
    try {
      await schema.validate(values, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) setErrors(collectValidationErrors(err));
      return false;
    }
  }, [schema]);

  return {
    formValues,
    setFormValues,
    errors,
    formError,
    setFormError,
    submitting,
    setSubmitting,
    handleChange,
    validate,
  };
};
